/**
 * components/riskTerminal/OrderTestBox.jsx
 *
 * upstox-082 — the "ORDER TEST" box: the fenced live order test's screen (services/upstox_broker/order_test.py).
 * Desktop Positions, the trader's Upstox account #1 only, and only while the server's switch is on (plus a plain
 * "Open test orders: N — Cancel" line while it is off and any are open). Every rule is enforced on the server; this
 * box only shows the fence (the two stocks, quantity 1, LIMIT intraday) and asks for a confirm step that repeats
 * everything before anything is sent. The server's own words are shown verbatim on a refusal. Colour only for a
 * refusal or a failure. Hidden entirely for anyone the server does not serve (its 404).
 *
 * upstox-083: the price is the trader's to set — the field starts EMPTY ("Your limit price"), the LTP is a tap-to-fill chip,
 * the buy / sell buttons stay disabled until a price is typed or the chip is used, the confirm step says the price against
 * the live price in words, and the field clears after a send. The fence is unchanged (it is the server's).
 *
 * Props: account (the screen's account), api (the three calls; the tests pass fakes), pollMs.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { C, FONT } from './theme';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';
export const ORDER_TEST_ACCOUNT_ID = 1;

async function call(method, path, body) {
  try {
    const res = await fetch(`${API}/upstox${path}`, {
      method, credentials: 'include',
      headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    let data = null;
    try { data = await res.json(); } catch (e) { data = null; }
    if (res.ok) return { ok: true, status: res.status, data };
    const d = data && data.detail;
    const message = d && typeof d === 'object' && d.message ? d.message : typeof d === 'string' ? d : 'The server refused this (HTTP ' + res.status + ').';
    return { ok: false, status: res.status, message };
  } catch (e) {
    return { ok: false, status: 0, message: 'Could not reach the server — nothing was sent.' };
  }
}

export const realApi = {
  state: () => call('GET', `/order-test?account=${ORDER_TEST_ACCOUNT_ID}`),
  place: (stock, side, price) => call('POST', '/order-test/place', { stock, side, price, account: ORDER_TEST_ACCOUNT_ID }),
  cancel: orderId => call('POST', '/order-test/cancel', { order_id: orderId, account: ORDER_TEST_ACCOUNT_ID }),
};

const fmt = v => (v == null ? '—' : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const hhmmss = ts => new Date(ts * 1000).toLocaleTimeString('en-IN', { hour12: false, timeZone: 'Asia/Kolkata' });
/** " — 9.6 % below the live price 995.55" (or above / at), so a price at or near the LTP is as obvious as one far from it */
export function versus(price, ltp) {
  if (ltp == null || !(ltp > 0)) return '';
  const pct = (price - ltp) / ltp * 100;
  if (Math.abs(pct) < 0.05) return ` — at the live price ${fmt(ltp)}`;
  return ` — ${Math.abs(pct).toFixed(1)} % ${pct < 0 ? 'below' : 'above'} the live price ${fmt(ltp)}`;
}
const small = { fontSize: '10px', letterSpacing: '0.1em', color: C.ink4 };
const btn = (on, extra) => ({ height: '28px', padding: '0 12px', background: on ? C.ink : '#fff', color: on ? '#fff' : C.ink, border: '1px solid ' + (on ? C.ink : C.line4),
  fontFamily: FONT, fontSize: '11px', letterSpacing: '0.08em', fontWeight: 600, cursor: 'pointer', ...extra });

export default function OrderTestBox({ account, api = realApi, pollMs = 3000 }) {
  const mine = !!account && account.broker === 'UPSTOX' && account.id === ORDER_TEST_ACCOUNT_ID;
  const [data, setData] = useState(null);                 // the server's answer; null = not served (hidden)
  const [prices, setPrices] = useState({});               // what the trader typed per stock
  const [confirm, setConfirm] = useState(null);           // { stock, side, price }
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState(null);                   // { ok, text }
  const alive = useRef(true);
  const busy = useRef(false);                             // one call at a time, whatever the clicks

  const load = useCallback(async () => {
    const r = await api.state();
    if (!alive.current) return;
    setData(r.ok ? r.data : null);                        // a 404 (anyone but the trader) hides the box
  }, [api]);

  useEffect(() => {
    alive.current = true;
    if (!mine) { setData(null); return undefined; }
    load();
    const t = setInterval(load, pollMs);
    return () => { alive.current = false; clearInterval(t); };
  }, [mine, load, pollMs]);

  if (!mine || !data) return null;
  const open = data.open_orders || [];
  if (!data.enabled && open.length === 0) return null;

  const send = async () => {
    if (busy.current || !confirm) return;                 // the button is disabled too; this is the second lock
    busy.current = true;
    setSending(true); setMsg(null);
    const r = await api.place(confirm.stock, confirm.side, confirm.price);
    busy.current = false;
    setSending(false); setConfirm(null);
    setPrices(p => ({ ...p, [confirm.stock]: '' }));                 // upstox-083: after a send the field clears
    if (!alive.current) return;
    if (r.ok && r.data) setMsg({ ok: !!r.data.ok, text: r.data.message });
    else setMsg({ ok: false, text: r.message });
    load();
  };
  const cancel = async o => {
    if (busy.current) return;
    busy.current = true;
    setSending(true); setMsg(null);
    const r = await api.cancel(o.order_id);
    busy.current = false;
    setSending(false);
    if (!alive.current) return;
    if (r.ok && r.data) setMsg({ ok: !!r.data.ok, text: r.data.message });
    else setMsg({ ok: false, text: r.message });
    load();
  };

  const priceFor = s => (prices[s.symbol] !== undefined ? prices[s.symbol] : '');          // empty until the trader sets it
  const priceOk = s => { const v = Number(priceFor(s)); return priceFor(s) !== '' && Number.isFinite(v) && v > 0; };
  const canTrade = data.enabled && data.window_open && data.route_set;

  return (
    <div data-order-test style={{ flex: '0 0 auto', background: C.paper2, borderBottom: '1px solid ' + C.line, padding: '10px 18px', fontFamily: FONT, color: C.ink }}>
      {data.enabled ? (
        <>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
            <span data-order-test-head style={{ fontSize: '11px', letterSpacing: '0.12em', fontWeight: 700 }}>ORDER TEST</span>
            <span style={small}>REAL ORDERS · ACCOUNT {(data.account && data.account.label) || 'UPSTOX'} · QUANTITY {data.limits.quantity} · LIMIT · INTRADAY · MOST {data.limits.max_position} PER STOCK</span>
            {!data.window_open && <span data-order-test-note style={{ fontSize: '11px', color: C.ink3 }}>Outside 09:15–15:20 IST on a weekday: no new orders.</span>}
            {!data.route_set && <span data-order-test-note style={{ fontSize: '11px', color: C.red }}>The order route is not set: no orders can be sent.</span>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
            {(data.stocks || []).map(s => (
              <div key={s.symbol} data-order-test-stock={s.symbol} style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                <span style={{ width: '90px', fontSize: '13px', fontWeight: 600 }}>{s.symbol}</span>
                <span style={{ width: '110px', fontSize: '12px', fontVariantNumeric: 'tabular-nums' }} data-tip={s.ltp_age_s != null ? 'price age ' + Math.round(s.ltp_age_s) + ' s' : 'no live price'}>LTP {fmt(s.ltp)}</span>
                <span style={{ width: '90px', fontSize: '12px', color: C.ink3 }}>HELD {s.position}</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: C.ink3 }}>
                  <span data-order-test-label>Your limit price</span>
                  <input aria-label={'Your limit price ' + s.symbol} placeholder="type a price" value={priceFor(s)} inputMode="decimal"
                    onChange={e => { setPrices(p => ({ ...p, [s.symbol]: e.target.value })); setConfirm(null); }}
                    style={{ width: '104px', height: '28px', border: '1px solid ' + C.ink, fontFamily: FONT, fontSize: '13px', padding: '0 8px', fontVariantNumeric: 'tabular-nums' }} />
                </label>
                {s.ltp != null && (
                  <button type="button" data-order-test-ltp-chip={s.symbol} disabled={sending}
                    onClick={() => { setPrices(p => ({ ...p, [s.symbol]: s.ltp.toFixed(2) })); setConfirm(null); }}
                    style={{ height: '24px', padding: '0 9px', background: '#fff', color: C.ink3, border: '1px dashed ' + C.line4, fontFamily: FONT, fontSize: '11px', cursor: 'pointer' }}>
                    {'use LTP ' + fmt(s.ltp)}
                  </button>
                )}
                {['BUY', 'SELL'].map(side => {
                  const off = !canTrade || sending || s.ltp == null || !priceOk(s);
                  return (
                    <button key={side} type="button" data-order-test-act={side + s.symbol} disabled={off}
                      style={{ ...btn(false), ...(off ? { opacity: 0.45, cursor: 'not-allowed' } : {}) }}
                      onClick={() => { setMsg(null); setConfirm({ stock: s.symbol, side, price: Number(priceFor(s)), ltp: s.ltp }); }}>{side} 1</button>
                  );
                })}
                <span data-order-test-range style={{ fontSize: '10px', color: C.ink4 }}>{s.buy_band ? 'Allowed range · BUY ' + fmt(s.buy_band[0]) + '–' + fmt(s.buy_band[1]) + ' · SELL ' + fmt(s.sell_band[0]) + '–' + fmt(s.sell_band[1]) : ''}</span>
              </div>
            ))}
          </div>
          {confirm && (
            <div data-order-test-confirm style={{ marginTop: '10px', padding: '14px 16px', background: '#fff', border: '2px solid ' + C.ink }}>
              <div data-order-test-confirm-text style={{ fontSize: '18px', fontWeight: 700, lineHeight: 1.3 }}>
                {`${confirm.side} 1 ${confirm.stock} at ${fmt(confirm.price)}${versus(confirm.price, confirm.ltp)}`}
              </div>
              <div data-order-test-confirm-detail style={{ fontSize: '12px', color: C.ink3, marginTop: '6px' }}>
                {`LIMIT · intraday · account ${(data.account && data.account.label) || 'UPSTOX'} · a real order, sent once, never retried`}
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <button type="button" data-order-test-send disabled={sending} onClick={send} style={{ ...btn(true), height: '36px', padding: '0 20px', fontSize: '12px', ...(sending ? { opacity: 0.5, cursor: 'wait' } : {}) }}>{sending ? 'SENDING…' : 'SEND'}</button>
                <button type="button" data-order-test-cancel-confirm disabled={sending} onClick={() => setConfirm(null)} style={{ ...btn(false), height: '36px', padding: '0 20px', fontSize: '12px' }}>CANCEL</button>
              </div>
            </div>
          )}
        </>
      ) : null}
      {open.length > 0 && (
        <div data-order-test-open style={{ marginTop: data.enabled ? '10px' : 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ fontSize: '11px', color: C.ink3 }}>{`Open test orders: ${open.length}`}</div>
          {open.map(o => (
            <div key={o.order_id} data-order-test-order={o.order_id} style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '12px', fontVariantNumeric: 'tabular-nums' }}>
              <span style={{ fontWeight: 600 }}>{`${o.side} ${o.quantity} ${o.stock || ''} @ ${fmt(o.price)}`}</span>
              <span style={{ color: C.ink3 }}>{o.status}</span>
              <button type="button" data-order-test-cancel={o.order_id} disabled={sending} onClick={() => cancel(o)} style={{ ...btn(false), height: '24px', ...(sending ? { opacity: 0.5 } : {}) }}>CANCEL</button>
            </div>
          ))}
        </div>
      )}
      {msg && <div data-order-test-result style={{ marginTop: '8px', fontSize: '12px', fontWeight: 500, color: msg.ok ? C.ink : C.red }}>{msg.text}</div>}
      {data.enabled && (data.log || []).length > 0 && (
        <div data-order-test-log style={{ marginTop: '10px', borderTop: '1px solid ' + C.line2, paddingTop: '6px' }}>
          <div style={small}>LAST {Math.min(10, data.log.length)} CALLS</div>
          {data.log.map(r => (
            <div key={r.id} style={{ display: 'flex', gap: '10px', fontSize: '11px', color: r.outcome && r.outcome.startsWith('refused') ? C.red : C.ink3, fontVariantNumeric: 'tabular-nums', marginTop: '2px' }}>
              <span>{hhmmss(r.ts)}</span><span>{r.action}</span><span>{r.side || ''} {r.stock || ''}</span><span>{r.price != null ? fmt(r.price) : ''}</span>
              <span style={{ fontWeight: 600 }}>{r.outcome}</span>{r.latency_ms != null && <span>{Math.round(r.latency_ms)} ms</span>}
              {r.detail && <span style={{ color: C.ink4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.detail}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
